import { formatCount, money, personName, toDate } from '../customers/common/customer-common'
import {
    AGING,
    DUE_RANGES,
    daysFromToday,
    dueDaysLabel,
    dueRangeOf,
    moneyCompact,
    shortDate,
    shiftIso,
} from '../collections/collectionFormat'

export { AGING, DUE_RANGES, dueDaysLabel, dueRangeOf, formatCount, money, moneyCompact, personName, shortDate, shiftIso, daysFromToday }

export const STATUS = {
    porcotizar: { id: 'porcotizar', label: 'Por cotizar', step: 0, className: 'bg-k-line text-k-ink-2' },
    cotizada: { id: 'cotizada', label: 'Cotizada', step: 1, className: 'bg-k-accent-soft text-k-accent-ink' },
    enviada: { id: 'enviada', label: 'Enviada al cliente', step: 2, className: 'bg-tone-orange text-tone-orange-ink' },
    renovada: { id: 'renovada', label: 'Renovada', step: 3, className: 'bg-k-ok-soft text-k-ok' },
    norenueva: { id: 'norenueva', label: 'No renueva', step: -1, className: 'bg-k-danger-soft text-k-danger-ink' },
}

export const STEPS = [
    { id: 'porcotizar', label: 'Por cotizar' },
    { id: 'cotizada', label: 'Cotizada' },
    { id: 'enviada', label: 'Enviada' },
    { id: 'renovada', label: 'Renovada' },
]

export const FORMA_PAGO = {
    Mensual: 'Mensual',
    Trimestral: 'Trimestral',
    Semestral: 'Semestral',
    Anual: 'Anual',
    Contado: 'Contado',
}

export const endDateOf = (poliza) => poliza?.fechaVigenciaA || poliza?.finVigenciaA

export const startDateOf = (poliza) => poliza?.fechaVigenciaDe || poliza?.finVigenciaDe

export const insuredName = (poliza) => {
    const fromParts = personName(poliza)
    if (fromParts) return fromParts
    return poliza?.contratante || '—'
}

export const ramoOf = (poliza) => {
    const value = poliza?.ramo
    if (!value) return poliza?.tipo || ''
    if (typeof value === 'string') return value
    return value.ramo || ''
}

export const primaOf = (poliza) => Number(poliza?.prima ?? poliza?.total ?? 0)

export const isClosedStatus = (statusId) => statusId === 'renovada' || statusId === 'norenueva'

export const defaultWorkflowStatus = (poliza) => {
    if (poliza?.polizaIDRenovo) return 'renovada'
    const key = String(poliza?.status || '').toLowerCase()
    if (['cancelada', 'precancelada', 'terminada'].includes(key)) return 'norenueva'
    if (key === 'renovada') return 'renovada'
    return 'porcotizar'
}

export const vigorNote = (days, statusId) => {
    const closed = isClosedStatus(statusId)
    if (days == null) return { dateClass: 'font-semibold text-k-ink', note: '', noteClass: 'text-k-muted', alertBg: 'bg-k-accent-soft', alertFg: 'text-k-accent-ink' }
    if (statusId === 'renovada') {
        return { dateClass: 'font-semibold text-k-muted', note: 'Nueva vigencia emitida', noteClass: 'text-k-ok', alertBg: 'bg-k-ok-soft', alertFg: 'text-k-ok' }
    }
    if (days < 0) {
        const late = Math.abs(days)
        return {
            dateClass: closed ? 'font-semibold text-k-muted' : 'font-bold text-k-danger',
            note: closed ? `Terminó hace ${late} ${late === 1 ? 'día' : 'días'}` : `Sin cobertura hace ${late} ${late === 1 ? 'día' : 'días'}`,
            noteClass: closed ? 'text-k-muted' : 'text-k-danger',
            alertBg: 'bg-k-danger-soft',
            alertFg: 'text-k-danger-ink',
        }
    }
    if (days === 0) {
        return {
            dateClass: 'font-bold text-k-danger',
            note: 'Termina hoy',
            noteClass: 'text-k-danger',
            alertBg: 'bg-k-danger-soft',
            alertFg: 'text-k-danger-ink',
        }
    }
    if (days <= 15) {
        return {
            dateClass: 'font-bold text-tone-orange-ink',
            note: `Quedan ${days} ${days === 1 ? 'día' : 'días'}`,
            noteClass: 'text-tone-orange-ink',
            alertBg: 'bg-tone-orange',
            alertFg: 'text-tone-orange-ink',
        }
    }
    return {
        dateClass: closed ? 'font-semibold text-k-muted' : 'font-semibold text-k-ink',
        note: `Quedan ${days} días`,
        noteClass: 'text-k-muted',
        alertBg: 'bg-k-accent-soft',
        alertFg: 'text-k-accent-ink',
    }
}

export const premiumDelta = (current, renewal) => {
    const base = Number(current || 0)
    const next = Number(renewal || 0)
    if (!base || !next) return { label: '', className: 'text-k-muted' }
    const pct = ((next - base) / base) * 100
    const label = `${pct >= 0 ? '▲' : '▼'} ${Math.abs(pct).toFixed(1)}%`
    if (pct > 5) return { label, className: 'text-k-danger-ink' }
    if (pct >= 0) return { label, className: 'text-tone-orange-ink' }
    return { label, className: 'text-k-ok' }
}

export const formaPagoLabel = (value) => {
    if (!value) return '—'
    return FORMA_PAGO[value] || String(value)
}

export const parseAmount = (value) => {
    const cleaned = String(value ?? '').replace(/[^0-9.]/g, '')
    const amount = Number.parseFloat(cleaned)
    return Number.isFinite(amount) ? amount : 0
}

export const buildCsv = (rows) => {
    const header = ['Póliza', 'Asegurado', 'Aseguradora', 'Vigencia', 'Prima', 'Estatus', 'Días']
    const lines = rows.map((row) => [
        row.noPoliza,
        row.name,
        row.aseguradora,
        row.fin,
        row.prima,
        row.statusLabel,
        row.days,
    ].map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
    return [header.join(','), ...lines].join('\n')
}

export const downloadCsv = (filename, content) => {
    const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
}

export const historySeed = (poliza, workflow) => {
    const items = []
    if (workflow.status === 'renovada') items.push({ text: 'Renovación emitida', when: 'Hoy' })
    if (workflow.renovPrima && workflow.status !== 'cotizada') items.push({ text: 'Cotización enviada al cliente', when: 'Reciente' })
    if (workflow.renovPrima) {
        const cia = workflow.renovCia || poliza.aseguradora || 'la aseguradora'
        items.push({ text: `Cotización recibida de ${cia}`, when: 'Reciente' })
    }
    items.push({ text: 'Aviso automático de renovación (60 días)', when: 'Hace 3 semanas' })
    return items
}

export const safeDate = (value) => {
    const date = toDate(value)
    return date || null
}
